import { makeStyles, Tabs, useMediaQuery } from '@material-ui/core';
import SchoolRoundedIcon from '@material-ui/icons/SchoolRounded';
import { APIClient } from '@src/client';
import { ShopifyShoppingIcon } from '@src/components/ShopifyViewer/SvgIcons';
import { appConfig } from '@src/config/appConfig';
import { integrationApps } from '@src/reducers/integrationApps';
import {
  fetchIntegrations,
  Integration,
  selectB2ChatJSIntegrations,
} from '@src/reducers/integrations';
import { fetchShopifyShop, fetchShopifyStores } from '@src/reducers/shopify';
import WidgetDefaultIcon from '@src/resources/images/b2chat-icon-48.png';
import { integrationAppsSelector } from '@src/selectors/integrationApps';
import { getMerchantId } from '@src/selectors/merchant';
import { preferencesSelect } from '@src/selectors/preferences';
import {
  getSelectedStoreId,
  hasShopifyIntegration,
} from '@src/selectors/shopify';
import { getShowWsCatalogueSideMenu } from '@src/selectors/wsCatalogue';
import { IntegrationApp } from '@src/types/integrations';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { LAYOUT_PADDING_WIDTH } from '../Layout/Utils';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChatBotIcon,
  ExclamationIcon,
  WsCatalogueOrdersIcon,
} from '../SvgIcons';
import TabButton from '../TabButton';
import { SideMenuTabs } from './constants';

const FLUENTLAB = 2199;

const useStyles = makeStyles(theme => ({
  spacing: {
    marginTop: '4px',
  },
  tabs: {
    backgroundColor: 'transparent',
    clipPath: 'inset(-10px -10px -10px 0)',
    '& .MuiTabs-scroller': {
      overflow: 'visible !important',
    },
    '& .Mui-selected': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  tabsClosed: {
    overflow: 'visible',
    '& .MuiTabs-scroller': {
      overflow: 'visible !important',
    },
    '& .MuiTabs-flexContainer > :first-child': {
      borderTopLeftRadius: '16px',
    },
    '& .MuiTabs-flexContainer > :last-child': {
      borderBottomLeftRadius: '16px',
    },
  },
  sideMenuContainer: {
    position: 'absolute',
    right: LAYOUT_PADDING_WIDTH,
    zIndex: 2,
  },
  bgClosed: {
    backgroundColor: theme.palette.background.paper,
  },
}));

interface ISideMenuWrapper {
  handleToggle: () => void;
  handleSelectOption: (index: number) => void;
  selectedOption: number | string;
  isOpen: boolean;
  withChatBotHistoryOpt?: boolean;
}
export interface ISideMenu {
  selectedOption: number | string;
  isOpen: boolean;
  withChatBotHistoryOpt?: boolean;
  hasShopifyIntegra: boolean;
  merchantId: number;
  showWsCatalogueSideMenu?: boolean;
  integrations: Integration[];
  apps: IntegrationApp[];
  devMode: boolean;
  // eslint-disable-next-line @typescript-eslint/ban-types
  handleChange: (_event: React.ChangeEvent<{}>, newValue: number) => void;
  handleToggle: () => void;
  handleSelectOption: (index: number) => void;
  applyClass: boolean;
}

const SideMenuWrapper: React.FC<ISideMenuWrapper> = ({
  handleToggle,
  handleSelectOption,
  selectedOption,
  isOpen,
  withChatBotHistoryOpt = false,
}) => {
  const dispatch = useDispatch();
  const isMaxWidth = useMediaQuery(
    `(max-width:${appConfig.maxWidthConsole}px)`
  );

  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    handleSelectOption(newValue);
    if (!isOpen) {
      handleToggle();
      handleSelectOption(newValue);
    }
  };

  const merchantId = useSelector(getMerchantId);
  const storeId = useSelector(getSelectedStoreId);
  const hasShopifyIntegra = useSelector(hasShopifyIntegration);
  const showWsCatalogueSideMenu = useSelector(getShowWsCatalogueSideMenu);
  const integrations = useSelector(selectB2ChatJSIntegrations);
  const apps = useSelector(integrationAppsSelector);
  const preferences = useSelector(preferencesSelect);

  const location = useLocation();

  const devMode = useMemo(
    () => new URLSearchParams(location.search).get('widgetdev') === '1',
    [location.search]
  );

  useEffect(() => {
    dispatch(fetchIntegrations());
    dispatch(fetchShopifyStores());
    dispatch(integrationApps.fetchIntegrationApps());
  }, [dispatch, merchantId]);

  useEffect(() => {
    if (storeId) dispatch(fetchShopifyShop({ storeId }));
  }, [dispatch, storeId]);

  useEffect(() => {
    if (!devMode && selectedOption === 'widget-dev') {
      handleSelectOption(SideMenuTabs.Contact);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devMode, selectedOption]);

  useEffect(() => {
    if (preferences?.chatViewerToken) {
      window.addEventListener('message', async event => {
        if (event.data.action === 'GET_AUTH_TOKEN') {
          // await b2chatclient.getJWT()
          //                   .removeJWT()
          //
          // 1. obtenemos el token desde la api y lo guardamos en la memoria
          // 2... obtenemos de la memoria: comprobamos expiracion
          // 3... interceptar el response cuando 401 -- withJWT
          //
          const token = await APIClient.getJWT();

          const viewerToken = preferences.chatViewerToken;

          const messageData = {
            action: 'SEND_AUTH_TOKEN',
            viewerToken,
            token,
          };
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          event.source?.postMessage(messageData, '*');
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences?.chatViewerToken]);

  return (
    <SideMenu
      selectedOption={selectedOption}
      isOpen={isOpen}
      withChatBotHistoryOpt={withChatBotHistoryOpt}
      hasShopifyIntegra={hasShopifyIntegra}
      merchantId={merchantId}
      integrations={integrations}
      apps={apps}
      devMode={devMode}
      handleChange={handleChange}
      handleToggle={handleToggle}
      handleSelectOption={handleSelectOption}
      showWsCatalogueSideMenu={showWsCatalogueSideMenu}
      applyClass={isMaxWidth}
    />
  );
};

export const SideMenu: React.FC<ISideMenu> = ({
  selectedOption,
  isOpen,
  withChatBotHistoryOpt,
  hasShopifyIntegra,
  merchantId,
  showWsCatalogueSideMenu,
  integrations,
  apps,
  devMode,
  handleChange,
  handleToggle,
  handleSelectOption,
  applyClass,
}) => {
  const { spacing, tabs, tabsClosed, sideMenuContainer, bgClosed } =
    useStyles();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
      className={`${applyClass ? sideMenuContainer : ''}`}
    >
      <Tabs
        orientation="vertical"
        value={isOpen ? selectedOption : false}
        onChange={handleChange}
        indicatorColor="primary"
        TabIndicatorProps={{
          style: { display: 'none' },
        }}
        className={`${tabs} ${!isOpen ? tabsClosed : ''}`}
      >
        <TabButton
          icon={isOpen ? ArrowRightIcon : ArrowLeftIcon}
          onClick={() => {
            handleToggle();
            if (!isOpen) handleSelectOption(SideMenuTabs.Contact);
          }}
          className={`${isOpen ? '' : bgClosed}`}
        />

        <TabButton
          isSelected={selectedOption === SideMenuTabs.Contact}
          value={SideMenuTabs.Contact}
          icon={ExclamationIcon}
          className={`${spacing} ${isOpen ? '' : bgClosed}`}
        />
        {withChatBotHistoryOpt && (
          <TabButton
            isSelected={selectedOption === SideMenuTabs.ChatBot}
            value={SideMenuTabs.ChatBot}
            icon={ChatBotIcon}
            className={`${spacing} ${isOpen ? '' : bgClosed}`}
          />
        )}
        {hasShopifyIntegra && (
          <TabButton
            isSelected={selectedOption === SideMenuTabs.Shopify}
            value={SideMenuTabs.Shopify}
            icon={ShopifyShoppingIcon(selectedOption === SideMenuTabs.Shopify)}
            className={`${spacing} ${isOpen ? '' : bgClosed}`}
          />
        )}
        {merchantId === FLUENTLAB && (
          <TabButton
            isSelected={selectedOption === SideMenuTabs.Fluentlab}
            icon={<SchoolRoundedIcon />}
            value={SideMenuTabs.Fluentlab}
            className={`${spacing} ${isOpen ? '' : bgClosed}`}
          />
        )}
        {showWsCatalogueSideMenu && (
          <TabButton
            isSelected={selectedOption === SideMenuTabs.WsCatalogue}
            icon={WsCatalogueOrdersIcon}
            value={SideMenuTabs.WsCatalogue}
            className={`${spacing} ${isOpen ? '' : bgClosed}`}
          />
        )}
        {devMode && (
          <TabButton
            value="widget-dev"
            icon={
              <img
                src={WidgetDefaultIcon}
                alt="widget-icon"
                style={{ width: 24 }}
                title="widget-dev"
              />
            }
            className={`${spacing} ${isOpen ? '' : bgClosed}`}
          />
        )}
        {integrations.map(
          item =>
            item.active && (
              <TabButton
                isSelected={selectedOption === item.id}
                key={item.id}
                value={item.id}
                icon={
                  <img
                    src={item.config.iconUrl as string}
                    alt={item.config.name}
                    style={{ width: 24 }}
                    title={item.config.name}
                  />
                }
                className={`${spacing} ${isOpen ? '' : bgClosed}`}
              />
            )
        )}

        {apps.map(
          item =>
            item.installation.active && (
              <TabButton
                isSelected={selectedOption === item.id}
                key={item.id}
                value={item.id}
                icon={
                  <img
                    src={item.iconUrl as string}
                    alt={item.name}
                    style={{
                      width: item.name === 'MercadoPago' ? 33 : 24,
                    }}
                    title={item.name}
                  />
                }
                className={`${spacing} ${isOpen ? '' : bgClosed}`}
              />
            )
        )}
      </Tabs>
    </div>
  );
};

export default SideMenuWrapper;
